import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';

export interface VariantData {
	type?: string;
	key?: string;
	namesMapKey?: string;
	generalSceneSlug?: string;
	generalAudioSlug?: string;
}

/**
 * Minimal editor for the `variant` object on scene/audio refs.
 * Only exposes two user-facing fields:
 * - key: which argument drives the variant (dropdown from argDefs)
 * - generalSceneSlug / generalAudioSlug: fallback scene/audio (dropdown from siblings)
 *
 * `type` (always NAME) and `namesMapKey` are auto-set and shown read-only.
 */
@Component({
	selector: 'app-variant-editor',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, Select],
	templateUrl: './variant-editor.html',
	styleUrl: './variant-editor.scss',
})
export class VariantEditorComponent {
	data = input<VariantData | null>(null);
	mode = input.required<'scene' | 'audio'>();
	argKeys = input<string[]>([]);
	siblingSlug = input<string[]>([]);

	dataChange = output<VariantData | null>();
	namesMapPopulate = output<{ key: string; names: string[] }>();

	protected argKeyOptions = computed(() =>
		this.argKeys().map(k => ({ label: k, value: k }))
	);

	protected siblingOptions = computed(() =>
		this.siblingSlug().map(s => ({ label: s, value: s }))
	);

	protected hasVariant(): boolean {
		return !!this.data();
	}

	protected removeVariant(): void {
		this.dataChange.emit(null);
	}

	protected setKey(value: string): void {
		const current = this.data() ?? { type: 'NAME' };
		this.dataChange.emit({ ...current, type: 'NAME', key: value || undefined });
	}

	protected setGeneralSlug(value: string): void {
		const current = this.data() ?? { type: 'NAME' };
		if (this.mode() === 'scene') {
			this.dataChange.emit({ ...current, type: 'NAME', generalSceneSlug: value || undefined });
		} else {
			this.dataChange.emit({ ...current, type: 'NAME', generalAudioSlug: value || undefined });
		}
	}
}
